// src/utils/notificationsFormat.js
// Helpers pentru citirea/normalizarea câmpurilor unei notificări, indiferent de
// forma exactă returnată de backend.

function readFirst(source, keys, fallback = null) {
   if (!source || typeof source !== "object") return fallback;
   for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== "") return value;
   }
   return fallback;
}

export function notificationId(n) {
   return readFirst(n, ["id", "_id", "notificationId", "notification_id"]);
}

export function notificationType(n) {
   return String(
      readFirst(n, ["type", "kind", "category", "notificationType"], "") || "",
   )
      .trim()
      .toUpperCase();
}

export function notificationTitle(n) {
   const explicit = readFirst(n, ["title", "subject", "heading"]);
   if (explicit) return String(explicit);
   return typeLabel(notificationType(n));
}

export function notificationBody(n) {
   return String(
      readFirst(n, ["message", "body", "text", "content", "description"], "") ||
         "",
   );
}

export function notificationCreatedAt(n) {
   return readFirst(n, [
      "createdAt",
      "created_at",
      "created",
      "timestamp",
      "sentAt",
      "sent_at",
      "date",
   ]);
}

export function isNotificationRead(n) {
   if (!n || typeof n !== "object") return false;
   if (n.isRead === true || n.read === true || n.seen === true) return true;
   if (n.isRead === false || n.read === false || n.seen === false) return false;
   const readAt = readFirst(n, ["readAt", "read_at", "seenAt", "seen_at"]);
   return Boolean(readAt);
}

// Grupele de tipuri conform cerințelor (lecții, examene, documente, video,
// reamintiri de programare). Menținem clasificare tolerantă la denumiri.
const TYPE_GROUPS = {
   LESSON: [
      "LESSON",
      "LESSON_REMINDER",
      "LESSON_CONFIRMATION",
      "RESERVATION",
      "BOOKING",
      "LECTIE",
   ],
   EXAM: [
      "EXAM",
      "PRACTICAL_EXAM",
      "EXAM_RESULT",
      "THEORY_EXAM",
      "EXAMEN",
   ],
   DOCUMENT: ["DOCUMENT", "DOCUMENT_READY", "DOC", "DOCUMENTE"],
   VIDEO: [
      "VIDEO",
      "LESSON_VIDEO",
      "RECOMMENDED_VIDEO",
      "VIDEO_RECOMMENDATION",
      "VIDEOCLIP",
   ],
   SCHEDULE: [
      "SCHEDULE",
      "SCHEDULE_REMINDER",
      "BOOKING_REMINDER",
      "PROGRAMARE",
      "REMINDER",
   ],
};

export function notificationGroup(n) {
   const type = notificationType(n);
   if (!type) return "GENERAL";
   for (const [group, values] of Object.entries(TYPE_GROUPS)) {
      if (values.includes(type)) return group;
      if (values.some((v) => type.includes(v) || v.includes(type))) {
         return group;
      }
   }
   return "GENERAL";
}

const GROUP_LABELS = {
   LESSON: "Lecție",
   EXAM: "Examen",
   DOCUMENT: "Documente",
   VIDEO: "Videoclip recomandat",
   SCHEDULE: "Reamintire programare",
   GENERAL: "Notificare",
};

export function typeLabel(type) {
   const group = (() => {
      const t = String(type || "").toUpperCase();
      for (const [g, values] of Object.entries(TYPE_GROUPS)) {
         if (values.includes(t)) return g;
      }
      return "GENERAL";
   })();
   return GROUP_LABELS[group] || GROUP_LABELS.GENERAL;
}

export function groupLabel(group) {
   return GROUP_LABELS[group] || GROUP_LABELS.GENERAL;
}

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

function parseYoutubeId(input) {
   const raw = String(input || "").trim();
   if (!raw) return "";
   if (YOUTUBE_ID_RE.test(raw)) return raw;

   let normalized = raw;
   if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;

   let url;
   try {
      url = new URL(normalized);
   } catch {
      return "";
   }

   const host = String(url.hostname || "")
      .replace(/^www\./i, "")
      .toLowerCase();

   if (host === "youtu.be") {
      const id = String(url.pathname || "")
         .split("/")
         .filter(Boolean)[0];
      return YOUTUBE_ID_RE.test(id) ? id : "";
   }

   if (
      host === "youtube.com" ||
      host.endsWith(".youtube.com") ||
      host === "youtube-nocookie.com" ||
      host.endsWith(".youtube-nocookie.com")
   ) {
      const fromQuery = String(url.searchParams.get("v") || "").trim();
      if (YOUTUBE_ID_RE.test(fromQuery)) return fromQuery;

      const parts = String(url.pathname || "")
         .split("/")
         .filter(Boolean);
      const markerIndex = parts.findIndex((segment) => {
         const value = String(segment || "").toLowerCase();
         return (
            value === "embed" ||
            value === "shorts" ||
            value === "live" ||
            value === "v"
         );
      });
      if (markerIndex >= 0) {
         const maybeId = String(parts[markerIndex + 1] || "").trim();
         if (YOUTUBE_ID_RE.test(maybeId)) return maybeId;
      }
   }

   return "";
}

export function notificationData(n) {
   const raw = readFirst(n, ["data", "payload", "meta", "metadata"]);
   if (!raw) return null;
   if (typeof raw === "object") return raw;
   if (typeof raw !== "string") return null;

   try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
   } catch {
      return null;
   }
}

export function notificationVideoMeta(n) {
   const group = notificationGroup(n);
   const type = notificationType(n);
   const isVideo =
      group === "VIDEO" || type === "LESSON_VIDEO" || type.includes("VIDEO");
   if (!isVideo) return null;

   const data = notificationData(n) || {};
   const youtubeUrl = String(
      readFirst(data, [
         "youtubeUrl",
         "youtube_url",
         "url",
         "videoUrl",
         "video_url",
         "link",
      ]) || "",
   ).trim();

   const youtubeId = String(
      readFirst(data, ["youtubeId", "youtube_id"]) ||
         parseYoutubeId(youtubeUrl) ||
         "",
   ).trim();

   if (!youtubeId) return null;

   const watchUrl =
      youtubeUrl || `https://www.youtube.com/watch?v=${youtubeId}`;

   let title = readFirst(data, ["videoTitle", "video_title", "title", "name"]);
   const body = notificationBody(n);
   const bodyPrefix = "Instructorul tău ți-a recomandat un videoclip:";
   if (!title && body.startsWith(bodyPrefix)) {
      title = body.slice(bodyPrefix.length).trim();
   }
   if (!title) title = notificationTitle(n);

   return {
      youtubeId,
      watchUrl,
      title: String(title || "Videoclip recomandat"),
      thumbUrl: `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`,
   };
}

const DEFAULT_VIDEO_LEAD = "Instructorul tău ți-a recomandat un videoclip:";

/**
 * Rândul de deasupra cardului cu videoclip. Textul notificării se poate
 * schimba din Mesaje automate, așa că nu ne bazăm pe o frază fixă: dacă textul
 * se termină cu titlul videoclipului (afișat oricum pe card), îl scoatem.
 */
export function videoNotificationLead(body, videoTitle) {
   const text = String(body || "").trim();
   const title = String(videoTitle || "").trim();
   if (title && text.endsWith(title)) {
      const lead = text.slice(0, -title.length).trim();
      if (lead) return lead;
   }
   if (text.startsWith(DEFAULT_VIDEO_LEAD)) return DEFAULT_VIDEO_LEAD;
   return text || DEFAULT_VIDEO_LEAD;
}

export function formatNotificationTime(value) {
   if (!value) return "";
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return "";

   const now = Date.now();
   const diffMs = now - d.getTime();
   const diffMin = Math.round(diffMs / 60000);

   if (diffMin < 1) return "Acum";
   if (diffMin < 60) return `Acum ${diffMin} min`;

   const diffH = Math.round(diffMin / 60);
   if (diffH < 24) return `Acum ${diffH} h`;

   const diffDays = Math.round(diffH / 24);
   if (diffDays < 7) return `Acum ${diffDays} z`;

   return d.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
   });
}
